import { Box } from "@mantine/core";
import { useAtom, useSetAtom } from "jotai";
import { type } from "os";
import { TFunction } from "react-i18next";
import { colors } from "theme";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import {
  accidentResponsibilityAtom,
  otherVehicleCircumstanceAtom,
  vehicleCircumstancePhAtom,
} from "atoms/accidentCircumstance";
import {
  amicableSignedAtom,
  authoritiesInterventionAtom,
  driverPresentAtom,
  informationOnOtherVehicleAtom,
  numInjuredPeopleAtom,
  numVehiclesInvolvedAtom,
} from "atoms/accidentInformation";
import { authoritiesPhotosAtom } from "atoms/authoritiesPhotos";
import { caiPhotosAtom } from "atoms/caiPhotos";
import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import { damagePhotosAtom } from "atoms/damagePhotos";
import { injuredPeoplePhotosAtom } from "atoms/injuredPeoplePhotos";
import { DEFAULT_INJURED_PERSONS, injuredPersonsAtom } from "atoms/injuredPersons";
import { insuranceTypeToActivateAtom } from "atoms/insuranceTypeToActivate";
import { naturalEventAtom } from "atoms/naturalEvents";
import {
  otherDriverFirstNameAtom,
  otherDriverLastNameAtom,
  otherDriverPersonalCodeAtom,
  otherVehicleInsuranceAtom,
  otherVehicleLicensePlateAtom,
  otherVehicleTypeAtom,
} from "atoms/otherVehicleInformation";
import { firstNamePhAtom, lastNamePhAtom, personalCodePhAtom } from "atoms/phPersonalDetails";
import { otherVehiclePointsOfImpactAtom, pointsOfImpactPhAtom } from "atoms/pointsOfImpactDigitalJourney";
import { bodyShopAtom } from "atoms/selectBodyShop";
import { typeOfClaimAtom } from "atoms/typeOfClaim";
import { vandalismSeverityAtom } from "atoms/vandalismSeverity";
import { mileageAtom } from "atoms/vehicleCondition/mileage";
import { vehicleConditionQuestionsAtom } from "atoms/vehicleCondition/questions";
import { vehiclePhotosAtom } from "atoms/vehiclePhotos";
import { windowsPOIAtom } from "atoms/windowsPOI";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { AccidentType } from "types/DigitalJourneySession";

export const djTypeOfClaimOptions = (t: TFunction) => [
  {
    label: t("digital_journey.type_of_claim.collision_between_vehicles"),
    value: AccidentType.COLLISION_BETWEEN_VEHICLES,
  },
  {
    label: t("digital_journey.type_of_claim.theft_fire_vandalism"),
    value: AccidentType.THEFT_FIRE_VANDALISM,
  },
  {
    label: t("digital_journey.type_of_claim.glass"),
    value: AccidentType.GLASS,
  },
  {
    label: t("digital_journey.type_of_claim.natural_events"),
    value: AccidentType.NATURAL_EVENTS,
  },
  {
    label: t("digital_journey.type_of_claim.kasko_collision"),
    value: AccidentType.KASKO,
  },
  {
    label: t("digital_journey.type_of_claim.other"),
    value: AccidentType.OTHER,
  },
];

export const DJTypeOfClaimPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [typeOfClaim, setTypeOfClaim] = useAtom(typeOfClaimAtom);
  const setNumVehiclesInvolved = useSetAtom(numVehiclesInvolvedAtom);
  const setDriverPresent = useSetAtom(driverPresentAtom);
  const setInformationOnOtherVehicle = useSetAtom(informationOnOtherVehicleAtom);
  const setNumInjuredPeople = useSetAtom(numInjuredPeopleAtom);
  const setAmicableSigned = useSetAtom(amicableSignedAtom);
  const setAuthoritiesIntervention = useSetAtom(authoritiesInterventionAtom);
  const setVehicleCircumstancePh = useSetAtom(vehicleCircumstancePhAtom);
  const setOtherVehicleCircumstance = useSetAtom(otherVehicleCircumstanceAtom);
  const setAccidentResponsibility = useSetAtom(accidentResponsibilityAtom);
  const setVehicleConditionQuestions = useSetAtom(vehicleConditionQuestionsAtom);
  const setMileage = useSetAtom(mileageAtom);
  const setPointsOfImpactPh = useSetAtom(pointsOfImpactPhAtom);
  const setPointsOfImpactOther = useSetAtom(otherVehiclePointsOfImpactAtom);
  const setFirstNamePh = useSetAtom(firstNamePhAtom);
  const setLastNamePh = useSetAtom(lastNamePhAtom);
  const setPersonalCodePh = useSetAtom(personalCodePhAtom);
  const setOtherVehicleLicensePlate = useSetAtom(otherVehicleLicensePlateAtom);
  const setOtherVehicleType = useSetAtom(otherVehicleTypeAtom);
  const setOtherVehicleInsurance = useSetAtom(otherVehicleInsuranceAtom);
  const setOtherDriverFirstName = useSetAtom(otherDriverFirstNameAtom);
  const setOtherDriverLastName = useSetAtom(otherDriverLastNameAtom);
  const setOtherDriverPersonalCode = useSetAtom(otherDriverPersonalCodeAtom);
  const setInjuredPersons = useSetAtom(injuredPersonsAtom);
  const setInjuredPeoplePhotos = useSetAtom(injuredPeoplePhotosAtom);
  const setCAIPhotos = useSetAtom(caiPhotosAtom);
  const setAuthoritiesPhotos = useSetAtom(authoritiesPhotosAtom);
  const setClaimHandlingDecision = useSetAtom(claimHandlingDecisionAtom);
  const setSelectedBodyShop = useSetAtom(bodyShopAtom);
  const setVehiclePhotos = useSetAtom(vehiclePhotosAtom);
  const setDamagePhotos = useSetAtom(damagePhotosAtom);
  const setWindowsPOI = useSetAtom(windowsPOIAtom);
  const setVandalismSeverity = useSetAtom(vandalismSeverityAtom);
  const setNaturalEvent = useSetAtom(naturalEventAtom);
  const setInsuranceTypeToActivate = useSetAtom(insuranceTypeToActivateAtom);

  const isDisabled = !typeOfClaim;

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Type of Claim - continue" });
    if (typeOfClaim === AccidentType.GLASS) {
      setNumVehiclesInvolved(null);
      setDriverPresent(null);
      setInformationOnOtherVehicle(null);
      setNumInjuredPeople(null);
      setAmicableSigned(null);
      setAuthoritiesIntervention(null);
      setVehicleCircumstancePh(null);
      setOtherVehicleCircumstance(null);
      setAccidentResponsibility(null);
      setVehicleConditionQuestions({
        isVehicleDrivable: undefined,
        haveAirbagsDeployed: undefined,
        windshieldShattered: undefined,
        canOpenAndCloseAllDoors: undefined,
      });
      setMileage("");
      setPointsOfImpactPh([]);
      setPointsOfImpactOther([]);
      setFirstNamePh("");
      setLastNamePh("");
      setPersonalCodePh("");
      setOtherVehicleLicensePlate("");
      setOtherVehicleType(null);
      setOtherVehicleInsurance("");
      setOtherDriverFirstName("");
      setOtherDriverLastName("");
      setOtherDriverPersonalCode("");
      setInjuredPersons(DEFAULT_INJURED_PERSONS);
      setInjuredPeoplePhotos([]);
      setCAIPhotos([]);
      setAuthoritiesPhotos([]);
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    } else setWindowsPOI([]);

    if (typeOfClaim === AccidentType.THEFT_FIRE_VANDALISM) {
      setNumVehiclesInvolved(null);
      setDriverPresent(null);
      setInformationOnOtherVehicle(null);
      setNumInjuredPeople(null);
      setAmicableSigned(null);
      setVehicleCircumstancePh(null);
      setOtherVehicleCircumstance(null);
      setAccidentResponsibility(null);
      setPointsOfImpactOther([]);
      setFirstNamePh("");
      setLastNamePh("");
      setPersonalCodePh("");
      setOtherVehicleLicensePlate("");
      setOtherVehicleType(null);
      setOtherVehicleInsurance("");
      setOtherDriverFirstName("");
      setOtherDriverLastName("");
      setOtherDriverPersonalCode("");
      setInjuredPersons(DEFAULT_INJURED_PERSONS);
      setInjuredPeoplePhotos([]);
      setCAIPhotos([]);
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    } else setVandalismSeverity(null);

    if (typeOfClaim === AccidentType.NATURAL_EVENTS) {
      setNumVehiclesInvolved(null);
      setDriverPresent(null);
      setInformationOnOtherVehicle(null);
      setNumInjuredPeople(null);
      setAmicableSigned(null);
      setAuthoritiesIntervention(null);
      setVehicleCircumstancePh(null);
      setOtherVehicleCircumstance(null);
      setAccidentResponsibility(null);
      setVehicleConditionQuestions({
        isVehicleDrivable: undefined,
        haveAirbagsDeployed: undefined,
        windshieldShattered: undefined,
        canOpenAndCloseAllDoors: undefined,
      });
      setMileage("");
      setPointsOfImpactOther([]);
      setFirstNamePh("");
      setLastNamePh("");
      setPersonalCodePh("");
      setOtherVehicleLicensePlate("");
      setOtherVehicleType(null);
      setOtherVehicleInsurance("");
      setOtherDriverFirstName("");
      setOtherDriverLastName("");
      setOtherDriverPersonalCode("");
      setInjuredPersons(DEFAULT_INJURED_PERSONS);
      setInjuredPeoplePhotos([]);
      setCAIPhotos([]);
      setAuthoritiesPhotos([]);
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    } else setNaturalEvent(null);

    if (typeOfClaim === AccidentType.KASKO) {
      setNumVehiclesInvolved(null);
      setDriverPresent(null);
      setInformationOnOtherVehicle(null);
      setNumInjuredPeople(null);
      setAmicableSigned(null);
      setAuthoritiesIntervention(null);
      setVehicleCircumstancePh(null);
      setOtherVehicleCircumstance(null);
      setAccidentResponsibility(null);
      setPointsOfImpactOther([]);
      setOtherVehicleLicensePlate("");
      setOtherVehicleType(null);
      setOtherVehicleInsurance("");
      setOtherDriverFirstName("");
      setOtherDriverLastName("");
      setOtherDriverPersonalCode("");
      setInjuredPersons(DEFAULT_INJURED_PERSONS);
      setInjuredPeoplePhotos([]);
      setCAIPhotos([]);
      setAuthoritiesPhotos([]);
      setSelectedBodyShop(null);
      setDamagePhotos([]);
      setVehiclePhotos([]);
      setClaimHandlingDecision(null);
    } else setInsuranceTypeToActivate(null);

    onNext();
  };

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          {t("digital_journey.type_of_claim.title")}
        </Text>
        <Box>
          <Text sizeVariant="sm" mb={8}>
            {t("digital_journey.type_of_claim.type")}
          </Text>
          <Flex gap={16} role="radiogroup" aria-labelledby="typeOfClaim">
            {djTypeOfClaimOptions(t).map((option) => (
              <Button
                aria-labelledby={`typeOfClaim-${option.label}-label`}
                aria-checked={typeOfClaim === option.value}
                role="radio"
                key={option.label}
                onClick={() => setTypeOfClaim(option.value)}
                typeVariant={typeOfClaim === option.value ? "primary" : "secondary"}
                sizeVariant="small"
                sx={{
                  "& div": {
                    justifyContent: "flex-start",
                    fontWeight: 400,
                    color: typeOfClaim === option.value ? colors.white : colors.grey[90],
                  },
                }}
              >
                <label id={`typeOfClaim-${option.label}-label`}>{option.label}</label>
              </Button>
            ))}
          </Flex>
        </Box>
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};
